/**
 * Files dropped on the chat, or chosen with the composer's upload button (MAR-126), out of
 * `ChatPanel.vue` (MAR-948). Saved to the "Chat uploads" folder — always persisted, since the agent
 * needs them indexed — attached as context chips, and tracked until each is ready.
 *
 * The one upload path that deliberately does NOT go through `uploadTarget` (AlexAmin/marcus#83).
 * That store exists because five surfaces uploaded into a folder and stopped, filing the document
 * under nothing. This path never had that hole: `attachDocument` writes `conversation_documents`, and
 * on a thread with no row yet `createConversation` flushes the ids when it mints one. Routing it
 * through the store anyway would put a dialog in front of a question with one answer, interrogate a
 * brand-new chat for real (no `conversationId` to pre-select), and lose what is chat-specific here:
 * the optimistic `pending-…` chips, the "ya lo tenías" `duplicate` flag, the chat-uploads folder and
 * opening the documents drawer on the queue.
 */
import { ref } from 'vue'
import { partitionAllowedFiles } from '~~/shared/allowedFileTypes'
import { CHAT_UPLOADS_PATH, CHAT_UPLOADS_FOLDER_NAME } from '~~/shared/chatUploads'
import type { AttachableDocument } from '~/composables/web-chat/useChatAttachments'

/** One created (or recognised-as-duplicate) upload, as the indexing store returns it. */
type UploadedItem = {
  id: string; name?: string | null; file_name?: string | null; status?: string | null
  duplicate?: boolean; reindexed?: boolean
}

export interface ChatUploadsDeps {
  attachDocument: (item: AttachableDocument) => void
  detachDocument: (id: string) => void
  documents: {
    ensureFolder: (name: string, path: string) => Promise<unknown>
    remove: (ids: string[]) => void
    refreshAll: () => Promise<void>
  }
  upload: (files: File[], opts: { path: string; source: 'chat' }) => Promise<UploadedItem[]>
  /** Shows where the work went — the documents drawer, on its indexing queue. */
  openDocsDrawer: () => void
  t: (key: string, params?: Record<string, unknown>) => string
}

export function useChatUploads(deps: ChatUploadsDeps) {
  const { attachDocument, detachDocument, documents, upload, openDocsDrawer, t } = deps

  /** A disallowed file's name(s) from the most recent drop/pick, shown by the composer (#804). */
  const uploadError = ref('')

  /** Optimistic chips have client-minted ids that never become rows, so their stub rows go too. */
  function detachTemp(temp: Array<{ id: string }>) {
    for (const chip of temp) detachDocument(chip.id)
    documents.remove(temp.map(chip => chip.id))
  }

  async function uploadFiles(files: File[]) {
    const { valid, disallowed } = partitionAllowedFiles(files)
    // Named, never just dropped (#804): a `.zip` used to vanish here with nothing said.
    uploadError.value = disallowed.length
      ? t('documents.unsupportedTypesNamed', { names: disallowed.map(f => f.name).join(', ') })
      : ''
    if (valid.length === 0) return

    // Dropping a file starts minutes of background work, in a panel the user may have collapsed.
    openDocsDrawer()

    const temp = valid.map((f, i) => ({
      id: `pending-${Date.now()}-${i}`,
      name: f.name,
      file_name: f.name,
      status: 'uploading' as const,
    }))
    for (const chip of temp) attachDocument(chip)

    try {
      // Idempotent server-side: an existing folder of that name comes back as itself. Best-effort —
      // if it fails, the upload below fails with its own error and takes the catch.
      await documents.ensureFolder(CHAT_UPLOADS_FOLDER_NAME, '/').catch(() => {})
      const created = await upload(valid, { path: CHAT_UPLOADS_PATH, source: 'chat' })
      detachTemp(temp)
      for (const item of created) {
        // A `duplicate` was refused because we already hold these bytes — the payload describes the
        // EXISTING item. Attach it anyway: the user's intent (work with this file) is satisfied.
        attachDocument({
          id: item.id,
          name: item.name ?? item.file_name ?? '',
          file_name: item.file_name ?? null,
          // A duplicate still indexing keeps its status, so the spinner and the send gate track it.
          status: item.status ?? 'processing',
          // A retry of a failed document is not a refusal — don't label it "ya lo tenías".
          duplicate: item.duplicate === true && item.reindexed !== true,
        })
      }
      void documents.refreshAll()
    } catch (err) {
      detachTemp(temp)
      console.error('[marcus:chat] Drop upload failed:', err)
    }
  }

  return { uploadError, uploadFiles }
}
